import {
  ShieldCheck,
  CheckCircle,
  DollarSign,
  Home,
  ThumbsUp,
  Ruler,
  CalendarClock,
  Hammer,
  Users,
  Wrench,
} from 'lucide-react';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/hvac.js';



export default function HVACServicesPage() {
  const service = {
    title: 'HVAC Services',
    image: '/hero/best-hvac-services-in-los-angeles.webp',
    description:
      'Expert HVAC services by Modern Build. Stay comfortable year-round with AC, heating, and air quality solutions. Free estimate: (323) 300-4130',
    link: '/services/hvac',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

      // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "HVAC Services", "item": pageUrl }
    ]
  };

  return (
    <div className="bg-white">
    <>
      <Head>
        <title>HVAC Services in Los Angeles | Free Estimate | Modern Build</title>
        <meta name="title" content="HVAC Services in Los Angeles | Free Estimate | Modern Build" />
        <meta
          name="description"
          content="Expert HVAC services by Modern Build. Stay comfortable year-round with AC, heating, and air quality solutions. Free estimate: (323) 300-4130"
        />
        <meta
          name="keywords"
          content="HVAC Los Angeles, air conditioning repair, furnace installation, AC services, heating and cooling LA, Modern Build"
        />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/hvac" />

        <meta property="og:title" content="HVAC Services in Los Angeles | Free Estimate | Modern Build" />
        <meta
          property="og:description"
          content="Expert HVAC services by Modern Build. Stay comfortable year-round with AC, heating, and air quality solutions. Free estimate: (323) 300-4130"
        />
        <meta
          property="og:image"
          content="https://modernbuildremodel.com/hero/best-hvac-services-in-los-angeles.webp"
        />
        <meta
          property="og:url"
          content="https://modernbuildremodel.com/services/hvac"
        />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="HVAC Services in Los Angeles | Free Estimate | Modern Build" />
        <meta
          name="twitter:description"
          content="Expert HVAC services by Modern Build. Stay comfortable year-round with AC, heating, and air quality solutions. Free estimate: (323) 300-4130"
        />
        <meta
          name="twitter:image"
          content="https://modernbuildremodel.com/hero/best-hvac-services-in-los-angeles.webp"
        />
        <meta name="twitter:site" content="@modernbuildhome" />


        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'HVAC Services in Los Angeles | Free Estimate | Modern Build',
                description:
                  'Full-service HVAC installation and repair for Los Angeles homes. Energy-efficient climate control by Modern Build.',
                slug: '/services/hvac',
              })
            ),
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>

      <Header />
      <Hero category="hvac" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="HEATING & COOLING EXPERTS"
        title="HVAC Services That Keep Your Home Comfortable Year-Round"
        background="white"
        description="Whether you need a new air conditioning system, heating repair, or improved indoor air quality, Modern Build delivers trusted HVAC solutions across Los Angeles. We tailor our services to your home’s needs and climate demands."
        extraText="From high-efficiency systems to smart thermostats and ductwork upgrades, we install and service HVAC solutions that improve comfort, reduce energy bills, and extend system life."
        phone="(323) 300-4130"
        image1="/split/hvac-installation-los-angeles-home.webp"
        image2="/split/modern-hvac-system-maintenance.webp"
      />

<Benefits
  title="Why Choose Modern Build for HVAC Services?"
  subtitle="Expert climate control with efficiency and care"
  items={[
    {
      icon: <ShieldCheck className="w-8 h-8 text-primary" />,
      title: "Work Performed by Licensed, Bonded & Insured HVAC Technicians",
      href: "/about-us",
    },
    {
      icon: <CheckCircle className="w-8 h-8 text-primary" />,
      title: "Energy-Efficient Equipment",
    },
    {
      icon: <Wrench className="w-8 h-8 text-primary" />,
      title: "Full System Install & Repair",
    },
    {
      icon: <Ruler className="w-8 h-8 text-primary" />,
      title: "Custom Ductwork Design",
    },
    {
      icon: <Hammer className="w-8 h-8 text-primary" />,
      title: "Precision Installation",
    },
    {
      icon: <DollarSign className="w-8 h-8 text-primary" />,
      title: "Lower Utility Costs",
      href: "/services/energy-efficient-windows",
    },
    {
      icon: <ThumbsUp className="w-8 h-8 text-primary" />,
      title: "Smart Thermostat Integration",
    },
    {
      icon: <Home className="w-8 h-8 text-primary" />,
      title: "Year-Round Comfort",
    },
  ]}
/>


      <ContentBlock
        subtitle="LOS ANGELES HVAC PROFESSIONALS"
        title="Smart Heating & Cooling for Your Home"
        description="Our HVAC services are built for Los Angeles homes. Whether you're installing a new air conditioner or upgrading an outdated furnace, we use modern systems designed to save energy and increase comfort."
        extraText="We offer upfront estimates, transparent timelines, and expert service with every project — so you can breathe easier at home."
        buttonText="Get an HVAC Estimate"
        background="white"
      />

      <ProcessRoadmap
        title="Our HVAC Service Process"
        subtitle="Comfort made easy with every step"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "System Evaluation",
            description: "We assess your current HVAC system and home layout to identify the right solutions.",
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Customized Recommendations",
            description: "We propose tailored heating and cooling options that maximize efficiency and comfort.",
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Installation or Repair",
            description: "Our certified technicians install, replace, or repair systems with clean, efficient work.",
          },
          {
            icon: <CheckCircle className="w-10 h-10 text-primary" />,
            title: "Testing & Optimization",
            description: "We test airflow, balance temperature zones, and walk you through system operation.",
          },
        ]}
      />

      <Split
        subtitle="INDOOR COMFORT EXPERTS"
        title="Reliable Heating & Cooling in Los Angeles"
        background="white"
        description="Stay ahead of extreme temperatures with HVAC systems designed for Southern California. Our team ensures you get the right unit, proper airflow, and efficient performance in every season."
        extraText="We support everything from ductless mini splits and whole-home AC to furnace upgrades and smart thermostats."
        phone="(323) 300-4130"
        image1="/split/energy-efficient-air-conditioning-install.webp"
        image2="/split/residential-hvac-upgrade-los-angeles.webp"
        reverse={true}
      />

      <CTASection
        title="Book a Free HVAC Consultation"
        subtitle="Heating, cooling, and air quality you can trust"
        buttonText="Schedule My HVAC Quote"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="hvac" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}
